import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from 'src/api/rpc/client';
import type {
  CreateDraftRequest,
  CreateDraftResponse,
  DeleteDraftRequest,
  DeleteDraftResponse,
  FinalizeVersionRequest,
  FinalizeVersionResponse,
  ListDraftsResponse,
  ListVersionsResponse,
  PinVersionRequest,
  PinVersionResponse,
  VersionJsonDiffRequest,
  RegenerateChangelogRequest,
  UpdateVersionChangelogRequest,
  WorkflowDefinitionResponse,
  WorkflowVersionSummary,
} from '@erp/rpc';

/**
 * Version history for a workflow lineage. Returns every version (draft + finalized)
 * ordered by version number descending, with creator name resolved for display.
 *
 * Uses the orpc-keyed cache so it shares state with the route loader's
 * `ensureQueryData(orpc.workflows.listVersions.queryOptions(...))` call —
 * mutations only need to invalidate one key for both consumers to see fresh
 * data.
 */
export function useListWorkflowVersions(workflowId?: string) {
  return useQuery<ListVersionsResponse>({
    ...orpc.workflows.listVersions.queryOptions({
      input: { workflowId: workflowId ?? '' },
    }),
    enabled: !!workflowId,
  });
}

function listVersionsQueryKey(workflowId: string) {
  return orpc.workflows.listVersions.queryOptions({ input: { workflowId } })
    .queryKey;
}

/**
 * Drafts — defaults to the current user's drafts; pass `ownedByCurrentUser: false`
 * to see all drafts in the org (for admin/triage views).
 */
export function useListWorkflowDrafts(params?: {
  ownedByCurrentUser?: boolean;
}) {
  return useQuery<ListDraftsResponse>({
    queryKey: ['workflow-drafts', params],
    queryFn: async () => {
      return await rpcClient.workflows.listDrafts({
        ownedByCurrentUser: params?.ownedByCurrentUser,
      });
    },
  });
}

export function useCreateDraft() {
  const queryClient = useQueryClient();
  return useMutation<CreateDraftResponse, Error, CreateDraftRequest>({
    mutationFn: async (input) => {
      return await rpcClient.workflows.createDraft(input);
    },
    onSuccess: (data, input) => {
      const draft = data.definition;

      // Callers navigate straight to /v/$newVersion, whose route loader reads
      // `listVersions` (to resolve the version number to a definition id) and
      // `getDefinition`. Seed both from the response instead of awaiting a
      // refetch: two blocking round-trips there trip the router's pending
      // component, so entering edit mode flashes a full-page skeleton.
      // (`ensureQueryData` returns cached data even when stale, so the
      // background invalidations below don't reintroduce the wait.)
      queryClient.setQueryData(
        orpc.workflows.getDefinition.queryOptions({
          input: { workflowDefinitionId: draft.id },
        }).queryKey,
        draft,
      );
      queryClient.setQueryData<ListVersionsResponse>(
        listVersionsQueryKey(draft.workflowId),
        (prev) => {
          // No cached list yet — let the loader fetch it. Inventing a
          // single-entry list would hide the lineage's other versions.
          if (!prev) return prev;
          const summary: WorkflowVersionSummary = {
            id: draft.id,
            workflowId: draft.workflowId,
            parentVersionId: draft.parentVersionId,
            version: draft.version,
            status: draft.status,
            isPinned: draft.isPinned,
            changelog: draft.changelog,
            createdBy: draft.createdBy,
            createdAt: draft.createdAt,
            updatedAt: draft.updatedAt,
            freshness: data.freshness,
          };
          const versions =
            input.onExistingDraft === 'replace'
              ? prev.versions.filter(
                  (version) =>
                    version.id === draft.id ||
                    version.status !== 'draft' ||
                    version.createdBy !== draft.createdBy,
                )
              : prev.versions;
          const existing = versions.find((v) => v.id === draft.id);
          return {
            ...prev,
            versions: existing
              ? // `resumed: true` — the draft was already listed. Merge onto
                // the server row so display-only extras it carries
                // (createdByName, failingValidation) survive.
                versions.map((v) =>
                  v.id === draft.id ? { ...v, ...summary } : v,
                )
              : [summary, ...versions],
          };
        },
      );

      void queryClient.invalidateQueries({
        queryKey: listVersionsQueryKey(draft.workflowId),
      });
      void queryClient.invalidateQueries({ queryKey: ['workflow-drafts'] });
      void queryClient.invalidateQueries({
        queryKey: orpc.workflows.previewVersionDiff.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.workflows.getVersionJsonDiff.key(),
      });
    },
  });
}

export function useFinalizeVersion() {
  const queryClient = useQueryClient();
  return useMutation<FinalizeVersionResponse, Error, FinalizeVersionRequest>({
    mutationFn: async (input) => {
      return await rpcClient.workflows.finalizeVersion(input);
    },
    onSuccess: async (response) => {
      if (!response.ok) return;
      // Seed the orpc-keyed cache with the just-finalized definition so the
      // route loader's `ensureQueryData` reads fresh state without a network
      // round-trip. (ensureQueryData returns cached data even when stale, so
      // a plain invalidate wouldn't force a re-fetch before the loader runs.)
      queryClient.setQueryData(
        orpc.workflows.getDefinition.queryOptions({
          input: { workflowDefinitionId: response.definition.id },
        }).queryKey,
        response.definition,
      );
      void queryClient.invalidateQueries({
        queryKey: orpc.workflows.previewVersionDiff.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.workflows.getVersionJsonDiff.key(),
      });
      queryClient.invalidateQueries({
        queryKey: listVersionsQueryKey(response.definition.workflowId),
      });
      queryClient.invalidateQueries({ queryKey: ['workflow-drafts'] });
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', 'summaries'],
      });
    },
  });
}

export function usePinVersion() {
  const queryClient = useQueryClient();
  return useMutation<PinVersionResponse, Error, PinVersionRequest>({
    mutationFn: async (input) => {
      return await rpcClient.workflows.pinVersion(input);
    },
    onSuccess: async (response) => {
      if (!response.ok) return;
      const workflowId = response.definition.workflowId;
      void queryClient.invalidateQueries({ queryKey: ['workflow-drafts'] });

      // The pin changes `workflow.pinnedVersionId` AND flips `isPinned` on
      // (at least) two definition rows — the previously-pinned and the newly-
      // pinned. We don't have the fresh workflow row in the response, so we
      // can't `setQueryData` it directly. Force an awaited refetch so by the
      // time this handler resolves, the route loader's next `ensureQueryData`
      // call sees the new `pinnedVersionId` (otherwise the bare
      // `/workflows/$id` URL resolves to the OLD pinned version after pin).
      await queryClient.refetchQueries({
        queryKey: orpc.workflows.getWorkflow.queryOptions({
          input: { workflowId },
        }).queryKey,
      });

      // Flip `isPinned` on every cached definition in the lineage, not just
      // the newly-pinned one: the route loader's `ensureQueryData` serves
      // cached rows even when stale, so the previously-pinned version would
      // otherwise keep showing "Published".
      queryClient.setQueriesData<WorkflowDefinitionResponse>(
        { queryKey: orpc.workflows.getDefinition.key() },
        (prev) =>
          prev?.workflowId === workflowId
            ? { ...prev, isPinned: prev.id === response.definition.id }
            : prev,
      );
      queryClient.setQueryData(
        orpc.workflows.getDefinition.queryOptions({
          input: { workflowDefinitionId: response.definition.id },
        }).queryKey,
        response.definition,
      );

      void queryClient.invalidateQueries({
        queryKey: orpc.workflows.previewVersionDiff.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.workflows.getVersionJsonDiff.key(),
      });
      queryClient.invalidateQueries({
        queryKey: listVersionsQueryKey(workflowId),
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow-definitions', 'summaries'],
      });
    },
  });
}

/**
 * Discard a draft version. When the draft is the lineage's only version the
 * backend deletes the whole workflow with it (`workflowDeleted: true`) — a
 * versionless workflow can't be opened or branched from — so callers must
 * navigate away from the workflow, not to its overview.
 */
export function useDeleteDraft() {
  const queryClient = useQueryClient();
  return useMutation<DeleteDraftResponse, Error, DeleteDraftRequest>({
    mutationFn: async (input) => {
      return await rpcClient.workflows.deleteDraft(input);
    },
    onSuccess: (res) => {
      // The response doesn't carry the lineageId, so blow away every versions
      // cache entry and the drafts list. Cheap enough for a rare action.
      void queryClient.invalidateQueries({
        queryKey: orpc.workflows.previewVersionDiff.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.workflows.getVersionJsonDiff.key(),
      });
      queryClient.invalidateQueries({
        queryKey: orpc.workflows.listVersions.key(),
      });
      queryClient.invalidateQueries({ queryKey: ['workflow-drafts'] });
      if (res.workflowDeleted) {
        // The whole lineage went with the draft — drop it from the workflow
        // lists and group trees too.
        void queryClient.invalidateQueries({
          queryKey: ['workflow-definitions', 'summaries'],
        });
        void queryClient.invalidateQueries({
          queryKey: ['workflow-definitions'],
        });
        void queryClient.invalidateQueries({ queryKey: ['workflow-groups'] });
      }
    },
  });
}

export function useRegenerateChangelog() {
  const queryClient = useQueryClient();
  return useMutation<
    WorkflowDefinitionResponse,
    Error,
    RegenerateChangelogRequest
  >({
    mutationFn: async (input) => {
      return await rpcClient.workflows.regenerateChangelog(input);
    },
    onSuccess: (definition) => {
      queryClient.invalidateQueries({
        queryKey: listVersionsQueryKey(definition.workflowId),
      });
    },
  });
}

export function useUpdateVersionChangelog() {
  const queryClient = useQueryClient();
  return useMutation<
    WorkflowDefinitionResponse,
    Error,
    UpdateVersionChangelogRequest
  >({
    mutationFn: async (input) => {
      return await rpcClient.workflows.updateVersionChangelog(input);
    },
    onSuccess: (definition) => {
      queryClient.invalidateQueries({
        queryKey: listVersionsQueryKey(definition.workflowId),
      });
    },
  });
}

export function usePreviewVersionDiff(workflowDefinitionId?: string) {
  return useQuery({
    ...orpc.workflows.previewVersionDiff.queryOptions({
      input: { workflowDefinitionId: workflowDefinitionId ?? '' },
    }),
    enabled: !!workflowDefinitionId,
    staleTime: 0,
  });
}

export function useVersionJsonDiff(params?: VersionJsonDiffRequest) {
  return useQuery({
    ...orpc.workflows.getVersionJsonDiff.queryOptions({
      input: params ?? { fromVersionId: '', toVersionId: '' },
    }),
    enabled: !!params?.fromVersionId && !!params?.toVersionId,
    staleTime: 0,
  });
}
